/**
 * useGuestMeals Hook
 * 
 * Manages guest mode meal state and persistence.
 * Extracted from App.js to reduce complexity.
 */

import { useState, useCallback } from 'react';
import { normalizeForGuest, calculateMealTotals } from '../utils/normalizeMeal';

const STORAGE_KEY = 'guest_mode_data';

/**
 * Load initial state from localStorage
 */
const getInitialState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      return {
        meals: data.meals || [],
        mealCount: data.mealCount || data.meals?.length || 0,
        profile: data.profile || null,
        targets: data.targets || null
      };
    }
  } catch (err) {
    console.error('[useGuestMeals] Error loading from localStorage:', err);
  }
  return { meals: [], mealCount: 0, profile: null, targets: null };
};

/**
 * Hook for managing guest meal state
 * @returns {object} Guest meal state and handlers
 */
export const useGuestMeals = () => {
  // Use lazy initialization to load from localStorage
  const [guestMeals, setGuestMeals] = useState(() => getInitialState().meals);
  const [guestMealCount, setGuestMealCount] = useState(() => getInitialState().mealCount);
  const [guestProfile, setGuestProfile] = useState(() => getInitialState().profile);
  const [guestTargets, setGuestTargets] = useState(() => getInitialState().targets);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [lastGuestMealData, setLastGuestMealData] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // PERSISTENCE - Save to localStorage when state changes
  // ═══════════════════════════════════════════════════════════════════════════
  const persistGuestData = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        mealCount: guestMealCount,
        meals: guestMeals,
        profile: guestProfile,
        targets: guestTargets
      }));
    } catch (err) {
      console.error('[useGuestMeals] Error saving to localStorage:', err);
    }
  }, [guestMeals, guestMealCount, guestProfile, guestTargets]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Add a meal to guest storage
   * @param {object} mealData - Raw meal data
   * @returns {object} - Normalized meal that was added
   */
  const addGuestMeal = useCallback((mealData) => {
    const normalized = normalizeForGuest(mealData);
    
    // Use functional updates and persist with new values directly
    setGuestMeals(prev => {
      const updated = [...prev, normalized];
      // Persist immediately with the new values (avoid closure issue)
      const newCount = prev.length + 1;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          mealCount: newCount,
          meals: updated,
          profile: guestProfile,
          targets: guestTargets
        }));
        console.log('[useGuestMeals] Persisted to localStorage, meals:', newCount);
      } catch (err) {
        console.error('[useGuestMeals] Error saving to localStorage:', err);
      }
      return updated;
    });
    setGuestMealCount(prev => prev + 1);
    setLastGuestMealData(normalized);
    
    console.log('[useGuestMeals] Added meal:', normalized.food_items);
    return normalized;
  }, [guestProfile, guestTargets]);

  /**
   * Remove a meal from guest storage
   * @param {string} mealId 
   */
  const removeGuestMeal = useCallback((mealId) => {
    setGuestMeals(prev => {
      const updated = prev.filter(m => m.id !== mealId);
      // Persist immediately with the new values
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          mealCount: updated.length,
          meals: updated,
          profile: guestProfile,
          targets: guestTargets
        }));
        console.log('[useGuestMeals] Persisted after removal, meals:', updated.length);
      } catch (err) {
        console.error('[useGuestMeals] Error saving to localStorage:', err);
      }
      return updated;
    });
    setGuestMealCount(prev => Math.max(0, prev - 1));
  }, [guestProfile, guestTargets]);

  /**
   * Update guest profile
   * @param {object} profile 
   */
  const updateGuestProfile = useCallback((profile) => {
    setGuestProfile(profile);
    // Persist immediately
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        mealCount: guestMealCount,
        meals: guestMeals,
        profile: profile,
        targets: guestTargets
      }));
      console.log('[useGuestMeals] Persisted profile update');
    } catch (err) {
      console.error('[useGuestMeals] Error saving to localStorage:', err);
    }
  }, [guestMeals, guestMealCount, guestTargets]);

  /**
   * Update calculated targets
   * @param {object} targets 
   */
  const updateGuestTargets = useCallback((targets) => {
    setGuestTargets(targets);
    // Persist immediately
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        mealCount: guestMealCount,
        meals: guestMeals,
        profile: guestProfile,
        targets: targets
      }));
      console.log('[useGuestMeals] Persisted targets update');
    } catch (err) {
      console.error('[useGuestMeals] Error saving to localStorage:', err);
    }
  }, [guestMeals, guestMealCount, guestProfile]);

  /**
   * Clear all guest data (called after migration to real account)
   */
  const clearGuestData = useCallback(() => {
    setGuestMeals([]);
    setGuestMealCount(0);
    setGuestProfile(null);
    setGuestTargets(null);
    setLastGuestMealData(null);
    setIsGuestMode(false);
    localStorage.removeItem(STORAGE_KEY);
    console.log('[useGuestMeals] Cleared all guest data');
  }, []);

  /**
   * Enter guest mode
   */
  const enterGuestMode = useCallback(() => {
    setIsGuestMode(true);
    console.log('[useGuestMeals] Entered guest mode');
  }, []);

  /**
   * Exit guest mode (without clearing data - for migration)
   */
  const exitGuestMode = useCallback(() => {
    setIsGuestMode(false);
    console.log('[useGuestMeals] Exited guest mode');
  }, []);

  /**
   * Get data for migration to real account
   * @returns {object}
   */
  const getMigrationData = useCallback(() => {
    return {
      meals: guestMeals,
      profile: guestProfile,
      targets: guestTargets,
      mealCount: guestMealCount
    };
  }, [guestMeals, guestProfile, guestTargets, guestMealCount]);

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════════════════════════════
  const guestTotals = calculateMealTotals(guestMeals);

  return {
    // State
    guestMeals,
    guestMealCount,
    guestProfile,
    guestTargets,
    guestTotals,
    isGuestMode,
    lastGuestMealData,
    
    // Actions
    addGuestMeal,
    removeGuestMeal,
    updateGuestProfile,
    updateGuestTargets,
    clearGuestData,
    enterGuestMode,
    exitGuestMode,
    getMigrationData,
    
    // Direct setters (for complex scenarios and backward compatibility)
    setIsGuestMode,
    setGuestMeals,
    setGuestMealCount,
    setGuestProfile,
    setGuestTargets,
    setLastGuestMealData
  };
};

export default useGuestMeals;
